import { debounce } from 'lodash';

export const getMeetingTime = (seconds: boolean = false): string => {
  const options: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if(seconds) {
    options.second = "2-digit";
  }
  return new Date().toLocaleTimeString(undefined, options);
};
export const getMeetingTimeSeconds = (): string => {
  const seconds = `0${new Date().toLocaleTimeString(undefined, { second: "2-digit"})}`;
  return `${seconds.substring(seconds.length - 2, seconds.length)}`;  
};

/**
 * Converts a server timestamp (e.g. `startedAt`, `pausedAt`) into a reference
 * Date suitable for an "elapsed since" counter.
 *
 * Client clocks may lag behind the server, which makes server timestamps appear
 * to be in the future. An elapsed counter would then stay frozen at `00:00:00`
 * until the client clock catches up (up to a few minutes of clock skew).
 * Clamping the reference to "now" makes the counter start counting immediately
 * instead of staying stuck at 0.
 */
export const clampFutureTimestamp = (timestamp: string | Date, disabled = false): Date => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (disabled) return date;
  const now = new Date();
  return date.getTime() > now.getTime() ? now : date;
};

export const generateRandomId = (digit: number = 16): string => [...Array(digit)].map(() => Math.floor(Math.random() * 16).toString(16)).join("");

export const asyncDebounce = <T = any>(func: (...args: any[]) => Promise<T>, wait: number): (...args: any[]) => Promise<T> => {
  const resolves: ((value: T | PromiseLike<T>) => void)[] = [];
  const rejects: ((reason?: any) => void)[] = [];
  const debounced = debounce(async (args) => {
    try {
      const result = await func(...args);
      resolves.forEach((resolve) => resolve(result));
    } catch (error) {
      rejects.forEach((reject) => reject(error));
    }
  }, wait);

  // This is the function that will be bound by the caller, so it must contain the `function` keyword.
  function returnFunc(...args: any[]): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      resolves.push(resolve);
      rejects.push(reject);
      debounced(args);
    });
  }
  return returnFunc;
};

export const isIp = (value: string): boolean => {
  return (/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/).test(value);
}

export const isMediaStream = (provider: MediaProvider | null | undefined): provider is MediaStream => {
  if (!provider) return false;
  return 'id' in provider;
}

export const convertMsToHMS = (milliseconds: number): string => {
  const padTo2Digits = (num: number): string => {
    return num.toString().padStart(2, '0');
  }
  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  seconds = seconds % 60;
  minutes = seconds >= 30 ? minutes + 1 : minutes;
  minutes = minutes % 60;
  hours = hours % 24;
  if (!hours) return `${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
  return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
}

export const isHex = (str: string): boolean => {
  return Boolean(str.match(/^[0-9a-f]+$/i))
}

export const isMongoDBId = (str: string | undefined): boolean => {
  return str?.length === 24 && isHex(str);
}

export const dataURLToFile = (dataURL: string, filename = "") => {
  const dataIndexStart = dataURL.indexOf(",");
  const byteString = atob(dataURL.slice(dataIndexStart + 1));
  const mimeType = dataURL.slice(0, dataIndexStart).split(":")[1].split(";")[0];

  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], filename, { type: mimeType });
};

export function capitalizeFirstLetter(string: string) {
  if (!string) return string;
  if(string.length < 2) return string.toLocaleUpperCase();
  return string.charAt(0).toLocaleUpperCase() + string.slice(1).toLocaleLowerCase();
};

export function arrayShuffleInPlace<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function takeFromArrayAndLoop<T>(array: T[], index: number, quantity: number): T[] {
  const sliced = array.slice(index, index + quantity);
  if (sliced.length < quantity) {
    return [...sliced, ...array.slice(0, quantity - sliced.length)];
  }
  return sliced;
}

type TailArgs <T> = T extends [any, ...infer U] ? U : never;

export function multiDebounce (
  fn: (...args: any[]) => void, 
  ...options: TailArgs<Parameters<typeof debounce>>
) {
  const map = new Map<string, ReturnType<typeof debounce>>();
  return (id: string) => {
    let debounced: ReturnType<typeof debounce> | undefined;
    debounced = map.get(id);
    if (!debounced) {
      debounced = debounce(fn, ...options);
      map.set(id, debounced);
    }
    return debounced;
  }
}
  
export const APP_VERSION = import.meta.env.VITE_APP_VERSION;